import React from 'react';
import SEOHead from '../components/SEOHead';
import {
  Box,
  Container,
  Grid,
  Typography,
  Card,
  CardContent,
  Avatar,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  Stack,
  Paper,
  Chip,
} from '@mui/material';
import { motion } from 'framer-motion';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CodeIcon from '@mui/icons-material/Code';
import BuildIcon from '@mui/icons-material/Build';
import SchoolIcon from '@mui/icons-material/School';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import SpeedIcon from '@mui/icons-material/Speed';
import SecurityIcon from '@mui/icons-material/Security';

const MotionBox = motion(Box);
const MotionTypography = motion(Typography);
const MotionCard = motion(Card);
const MotionPaper = motion(Paper);

const skills = [
  'iOS App Development (Swift, SwiftUI)',
  'Android App Development (Kotlin, Java)',
  'React.js & Next.js Development',
  'Full-Stack Web Development',
  'UI/UX Design',
  'API Development & Integration',
  'Cloud Services (AWS, Firebase)',
  'Performance Optimization',
];

const values = [
  {
    icon: <LightbulbIcon />,
    title: 'Innovation',
    description: 'Always exploring new technologies and creative solutions',
    color: '#f59e0b',
  },
  {
    icon: <SpeedIcon />,
    title: 'Efficiency',
    description: 'Fast delivery without compromising on quality',
    color: '#14b8a6',
  },
  {
    icon: <SecurityIcon />,
    title: 'Reliability',
    description: 'Secure, scalable, and maintainable solutions',
    color: '#8b5cf6',
  },
  {
    icon: <RocketLaunchIcon />,
    title: 'Growth',
    description: 'Building solutions that scale with your business',
    color: '#ef4444',
  },
];

const workProcess = [
  {
    title: 'Discovery',
    description: 'Understanding your needs and project requirements in detail',
    color: '#14b8a6',
    icon: <LightbulbIcon />,
  },
  {
    title: 'Planning',
    description: 'Creating a comprehensive development strategy and timeline',
    color: '#0891b2',
    icon: <BuildIcon />,
  },
  {
    title: 'Development',
    description: 'Building your solution with regular updates and feedback',
    color: '#f97316',
    icon: <CodeIcon />,
  },
  {
    title: 'Delivery',
    description: 'Thorough testing and deployment of your project',
    color: '#10b981',
    icon: <RocketLaunchIcon />,
  },
];


const techStack = [
  'Shopify Apps', 'Shopify Plus', 'Liquid Templates', 'Shopify CLI', 'Polaris Design System',
  'Shopify GraphQL', 'Shopify REST API', 'Shopify Webhooks', 'App Extensions', 'Checkout Extensions',
  'React.js', 'Next.js', 'TypeScript', 'JavaScript',
  'Node.js', 'Express.js', 'Python',
  'Vercel', 'AWS Hosting', 'SSL Certificates', 'CDN Setup', 'Domain Management',
  'Stripe', 'PayPal', 'Razorpay', 'Mollie', 'Klarna',
  'Apple Pay', 'Google Pay', 'Samsung Pay', 'Cryptocurrency Payments',
  'Twilio SMS', 'AWS SNS', 'Firebase Messaging', 'AWS SES', 'SendGrid', 'WhatsApp API', 'Telegram Bot', 'Slack Integration', 
  'Swift', 'Kotlin', 'React Native', 'Flutter',
  'AWS', 'Google Cloud', 'Firebase', 
  'MongoDB', 'PostgreSQL', 'MySQL', 'Shopify Metafields',
  'Docker', 'Kubernetes', 'CI/CD', 'GitHub Actions',
  'GraphQL', 'REST APIs', 'WebSockets', 'OAuth', 'JWT Authentication',
  'Tailwind CSS', 'Material-UI', 'Bootstrap', 'Styled Components', 'SCSS'
];


const About = () => {
  const theme = useTheme();

  return (
    <>
      <SEOHead
        title="About DevHopes Labs - Expert Mobile & Web Development Team"
        description="Learn about DevHopes Labs - A passionate development team delivering high-quality mobile and web development solutions. Specializing in iOS, Android, React.js, Next.js, and Shopify development with years of experience."
        keywords="about DevHopes Labs, mobile app developers, web developers, iOS developers, Android developers, React.js experts, Next.js specialists, Shopify developers, Netherlands developers, professional development team, software development experience"
        url="/about"
        type="website"
        schemaType="AboutPage"
      />

      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #0f766e 0%, #14b8a6 50%, #5eead4 100%)',
          py: { xs: 10, md: 12 },
          mt: { xs: 7, md: 8 },
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.1"%3E%3Ccircle cx="30" cy="30" r="2"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
          },
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={{ xs: 4, md: 8 }} alignItems="center">
            <Grid item xs={12} md={6}>
              <MotionBox
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                sx={{ position: 'relative', zIndex: 1 }}
              >
                <MotionTypography
                  variant="h1"
                  sx={{
                    fontSize: { xs: '2.5rem', md: '4rem' },
                    fontWeight: 800,
                    mb: 3,
                    color: 'white',
                    textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                  }}
                >
                  About DevHopes Labs
                </MotionTypography>
                <Typography 
                  variant="h5" 
                  sx={{ 
                    mb: 4,
                    fontSize: { xs: '1.2rem', md: '1.5rem' },
                    lineHeight: 1.6,
                    color: 'rgba(255,255,255,0.9)',
                    fontWeight: 300,
                  }}
                >
                  Passionate developer turning innovative ideas into exceptional digital experiences
                </Typography>
                <Typography 
                  variant="body1" 
                  sx={{
                    fontSize: { xs: '1rem', md: '1.1rem' },
                    lineHeight: 1.8,
                    maxWidth: 500,
                    color: 'rgba(255,255,255,0.8)',
                  }}
                >
                  As the solo developer behind DevHopes Labs, I bring years of experience in mobile and web development,
                  delivering high-quality solutions that help businesses thrive in the digital world.
                </Typography>
              </MotionBox>
            </Grid>
            <Grid item xs={12} md={6}>
              <MotionBox
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  mt: { xs: 4, md: 0 },
                  position: 'relative',
                  zIndex: 1,
                }}
              >
                <Box
                  sx={{
                    position: 'relative',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: -20,
                      left: -20,
                      right: -20,
                      bottom: -20,
                      borderRadius: '50%',
                      background: 'linear-gradient(45deg, rgba(255,255,255,0.2), rgba(255,255,255,0.1))',
                      zIndex: -1,
                    }
                  }}
                >
                  <Avatar
                    sx={{
                      width: { xs: 200, md: 280 },
                      height: { xs: 200, md: 280 },
                      background: 'linear-gradient(135deg, #14b8a6 0%, #0f766e 100%)',
                      boxShadow: '0 20px 40px rgba(0,0,0,0.3)',
                    }}
                  >
                    <img src={`${process.env.PUBLIC_URL}images/logo.png`} alt="DevHopes Labs" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  </Avatar>
                </Box>
              </MotionBox>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Values Section */}
      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: 'background.default' }}>
        <Container maxWidth="lg">
          <MotionBox
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}
          >
            <Typography
              variant="h2"
              sx={{ 
                mb: 3, 
                fontWeight: 700,
                fontSize: { xs: '2.2rem', md: '3rem' },
                background: 'linear-gradient(135deg, #14b8a6 0%, #0f766e 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              My Core Values
            </Typography>
            <Typography
              variant="h6"
              color="text.primary"
              sx={{ maxWidth: 600, mx: 'auto', fontWeight: 400 }}
            >
              The principles that guide every project and client relationship
            </Typography>
          </MotionBox>
          
          <Grid container spacing={{ xs: 3, md: 4 }} justifyContent="center">
            {values.map((value, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                  <CardContent sx={{ p: { xs: 3, md: 4 } }} align="center">
                    <Box
                      sx={{
                        width: 80,
                        height: 80,
                        borderRadius: '50%',
                        background: `linear-gradient(135deg, ${value.color}20 0%, ${value.color}40 100%)`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mx: 'auto',
                        mb: 3,
                        border: `2px solid ${value.color}30`,
                      }}
                    >
                      {React.cloneElement(value.icon, {
                        sx: { fontSize: 40, color: value.color }
                      })}
                    </Box>
                    <Typography
                      variant="h6"
                      sx={{ 
                        fontWeight: 600, 
                        mb: 2,
                        fontSize: { xs: '1.2rem', md: '1.3rem' },
                      }}
                    >
                      {value.title}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      color="text.secondary"
                      sx={{
                        lineHeight: 1.7,
                        fontSize: { xs: '0.95rem', md: '1rem' },
                      }}
                    >
                      {value.description}
                    </Typography>
                  </CardContent>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Skills & Tech Stack Section */}
      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: 'background.paper' }}>
        <Container maxWidth="lg">
          <Grid container spacing={{ xs: 4, md: 8 }} alignItems="center">
            <Grid item xs={12} md={6}>
              <MotionBox
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
              >
                <Typography
                  variant="h3"
                  sx={{ 
                    mb: 4, 
                    fontWeight: 700,
                    fontSize: { xs: '2rem', md: '2.5rem' },
                    background: 'linear-gradient(135deg, #14b8a6 0%, #0f766e 100%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  Technical Expertise
                </Typography>
                <List sx={{ p: 0 }}>
                  {skills.map((skill, index) => (
                    <MotionBox
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                    >
                      <ListItem sx={{ px: 0, py: 1.5 }}>
                        <ListItemIcon sx={{ minWidth: 40 }}>
                          <CheckCircleIcon 
                            sx={{ 
                              color: '#14b8a6',
                              fontSize: 24,
                            }} 
                          />
                        </ListItemIcon>
                        <ListItemText 
                          primary={skill}
                          primaryTypographyProps={{
                            fontSize: { xs: '1rem', md: '1.1rem' },
                            fontWeight: 500,
                            color: 'text.primary',
                          }}
                        />
                      </ListItem>
                    </MotionBox>
                  ))}
                </List>
              </MotionBox>
            </Grid>
            <Grid item xs={12} md={6}>
              <MotionBox
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                <Typography
                  variant="h4"
                  sx={{ 
                    mb: 4, 
                    fontWeight: 600,
                    fontSize: { xs: '1.5rem', md: '2rem' },
                  }}
                >
                  Technology Stack
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 4 }}>
                  {techStack.map((tech, index) => (
                    <MotionBox
                      key={tech}
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.5, delay: index * 0.05 }}
                    >
                      <Chip
                        label={tech}
                        sx={{
                          bgcolor: 'primary.50',
                          color: 'primary.main',
                          fontWeight: 600,
                          fontSize: '0.9rem',
                          px: 1,
                          py: 2,
                          '&:hover': {
                            bgcolor: 'primary.100',
                            transform: 'scale(1.05)',
                          },
                          transition: 'all 0.3s ease',
                        }}
                      />
                    </MotionBox>
                  ))}
                </Box>
                
                <MotionPaper
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                  elevation={0}
                  sx={{
                    p: 4,
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
                    border: '1px solid rgba(14, 184, 166, 0.1)',
                  }}
                >
                  <Typography 
                    variant="body1" 
                    sx={{
                      lineHeight: 1.8,
                      fontSize: { xs: '1rem', md: '1.1rem' },
                      color: 'text.primary',
                      fontStyle: 'italic',
                    }}
                  >
                    "I believe in using the right tool for the right job. My diverse skill set allows me to choose the best technologies for each project, ensuring optimal performance and maintainability."
                  </Typography>
                </MotionPaper>
              </MotionBox>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Work Process Section */}
      <Box 
        sx={{ 
          py: { xs: 8, md: 12 }, 
          background: 'linear-gradient(135deg, #0f766e 0%, #14b8a6 50%, #5eead4 100%)',
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'url("data:image/svg+xml,%3Csvg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.05"%3E%3Cpath d="M20 20c0-5.5-4.5-10-10-10s-10 4.5-10 10 4.5 10 10 10 10-4.5 10-10zm10 0c0-5.5-4.5-10-10-10s-10 4.5-10 10 4.5 10 10 10 10-4.5 10-10z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
          },
        }}
      >
        <Container maxWidth="lg">
          <MotionBox
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            sx={{ textAlign: 'center', mb: { xs: 6, md: 8 }, position: 'relative', zIndex: 1 }}
          >
            <Typography
              variant="h2"
              sx={{ 
                mb: 3, 
                fontWeight: 700,
                fontSize: { xs: '2.2rem', md: '3rem' },
                color: 'white',
                textShadow: '0 2px 4px rgba(0,0,0,0.3)',
              }}
            >
              How I Work
            </Typography>
            <Typography
              variant="h6"
              sx={{ 
                maxWidth: 600, 
                mx: 'auto', 
                fontWeight: 400,
                color: 'rgba(255,255,255,0.9)',
              }}
            >
              A proven process that delivers exceptional results every time
            </Typography>
          </MotionBox>
          
          <Grid container spacing={{ xs: 3, md: 4 }} justifyContent="center">
            {workProcess.map((step, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
              <MotionBox
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                sx={{ 
                  textAlign: 'center',
                  position: 'relative',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  '&::after': index < workProcess.length - 1 ? {
                    content: '""',
                    position: 'absolute',
                    top: { xs: '100%', md: '40%' },
                    right: { xs: '50%', md: '-30px' },
                    transform: { xs: 'translateX(50%)', md: 'translateY(-50%)' },
                    width: { xs: '2px', md: '60px' },
                    height: { xs: '30px', md: '2px' },
                    bgcolor: 'primary.300',
                    display: { xs: 'none', md: 'block' },
                    zIndex: 1
                  } : {}
                }}
              >
                <Avatar
                  sx={{
                    width: { xs: 80, md: 100 },
                    height: { xs: 80, md: 100 },
                    bgcolor: step.color,
                    mx: 'auto',
                    mb: 3,
                    background: `linear-gradient(135deg, ${step.color} 0%, ${step.color}dd 100%)`,
                    boxShadow: `0 8px 24px ${step.color}40`,
                  }}
                >
                  {step.icon}
                </Avatar>
                
                <Typography
                  variant="h5"
                  sx={{ 
                    fontWeight: 600, 
                    mb: 2,
                    fontSize: { xs: '1.3rem', md: '1.4rem' },
                    color: 'white'
                  }}
                >
                  {step.title}
                </Typography>
                
                <Typography 
                  variant="body1" 
                  color="text.secondary"
                  sx={{ 
                    lineHeight: 1.7,
                    fontSize: { xs: '1rem', md: '1rem' },
                    maxWidth: 300,
                    mx: 'auto',
                    textAlign: 'center',
                    color: 'white'
                  }}
                >
                  {step.description}
                </Typography>
              </MotionBox>
            </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
    </>
  );
};

export default About; 